import { groupBy, isEqual, isString } from 'lodash';
import { Color, getDefaultCSSColor } from '@ardoq/color-helpers';
import {
  APIComponentAttributes,
  APIComponentType,
  APIModelAttributes,
  APIReferenceType,
  APIWorkspaceAttributes,
  ArdoqId,
  ConditionalFormattingAttributes,
} from '@ardoq/api-types';
import { diffModeUpdated } from 'scope/actions';
import { combineLatest, merge } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { components$ } from 'streams/components/components$';
import { action$, dispatchAction, ofType } from '@ardoq/rxbeach';
import {
  addStylesToStyleSheet,
  ResetEntityCss,
  StyledEntityCss,
} from './actions';
import { FILTER_STYLES_KEY } from './consts';
import { conditionalFormattingFilters$ } from 'streams/filters/conditionalFormattingFilters$';
import {
  getLinkedWorkspaceIds,
  LinkedWorkspaces,
  linkedWorkspaces$,
} from 'streams/linkedWorkspaces$';
import { applicationStarted } from 'streams/context/ContextActions';
import { context$ } from 'streams/context/context$';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { VIRTUAL_MODEL_ID } from '@ardoq/global-consts';
import { filterOperations, modelOperations } from '@ardoq/core';
import { workspaceAndModel$ } from 'streams/workspaceAndModel$';
import {
  entityCssOperations,
  type FormattingColors,
  scopeDataOperations,
  type WorkspaceWithTypes,
} from '@ardoq/scope-data';
import {
  LoadedGraphWithViewpointMode,
  WorkspaceAndModelLookup,
} from '@ardoq/data-model';
import { isTruthy } from '@ardoq/common-helpers';

type CssStateInputs = {
  workspaceIds: ArdoqId[];
  linkedWorkspaces: LinkedWorkspaces;
  workspaceAndModel: WorkspaceAndModelLookup;
  components: APIComponentAttributes[];
  loadedGraph: LoadedGraphWithViewpointMode;
  formattingFilters: ConditionalFormattingAttributes[];
};

type WorkspaceStyleArgs = {
  componentStyleArgs: ComponentStyleSheetArgs[];
  modelStyleArgs: ModelStyleArgs;
};

const toModelStyleArgsFromWorkspace = (
  workspace: APIWorkspaceAttributes,
  componentTypes: APIComponentType[],
  referenceTypes: APIReferenceType[]
): ModelStyleArgs => {
  const modelId = workspace.componentModel;

  return {
    modelId,
    componentTypes,
    referenceTypes,
  };
};

const resolveWorkspaceStyleArgs = (
  workspaceAndTypes: WorkspaceWithTypes[],
  components: APIComponentAttributes[]
): WorkspaceStyleArgs[] => {
  if (workspaceAndTypes.length === 0) return [];

  const componentsByWorkspace = groupBy(components, c => c.rootWorkspace);

  return workspaceAndTypes.map(
    ({ workspace, componentTypes, referenceTypes }) => {
      const workspaceComponents = componentsByWorkspace[workspace._id] || [];

      return {
        modelStyleArgs: toModelStyleArgsFromWorkspace(
          workspace,
          componentTypes,
          referenceTypes
        ),
        componentStyleArgs: workspaceComponents.map(
          resolveComponentStyleSheetEntryArgs
        ),
      };
    }
  );
};

const virtualModelAsModelStyleArgs = (
  referenceTypes: APIReferenceType[]
): ModelStyleArgs => {
  return {
    componentTypes: [],
    referenceTypes,
    modelId: VIRTUAL_MODEL_ID,
  };
};

const resolveAllWorkspaceIds = (
  workspaceIds: ArdoqId[],
  linkedWorkspaces: LinkedWorkspaces
) => {
  {
    return [
      ...workspaceIds,
      ...getLinkedWorkspaceIds(workspaceIds, linkedWorkspaces),
    ];
  }
};

const toResolvedWorkspacesWithTypes = (
  workspaceIds: ArdoqId[],
  linkedWorkspaces: LinkedWorkspaces,
  workspaceAndModel: WorkspaceAndModelLookup
): WorkspaceWithTypes[] =>
  resolveAllWorkspaceIds(workspaceIds, linkedWorkspaces)
    .map(id => scopeDataOperations.getWorkspaceAndModel(workspaceAndModel, id))
    .filter(isTruthy)
    .map(scopeDataOperations.toWorkspaceWithTypes);

export const startHandlingCss = () => {
  // Workspace CSS is a pure function of (workspaceIds, linkedWorkspaces,
  // workspaceAndModel, components, formattingFilters, scenarioDiffMode).
  // Collapsed here to one derived stream with distinctUntilChanged on the
  // computed output, so identical CSS is only dispatched once. See ARD-32076.
  //
  // `components` is threaded through (instead of read synchronously via
  // componentInterface inside the pipeline) so that whatever `components$`
  // currently holds is the authoritative input for this run — no race with
  // `componentInterface` reads outside the pipeline.
  const cssStateInputs$ = combineLatest({
    workspaceIds: context$.pipe(
      map(({ workspaceIds }) => workspaceIds),
      distinctUntilChanged(isEqual)
    ),
    linkedWorkspaces: linkedWorkspaces$,
    workspaceAndModel: workspaceAndModel$,
    components: components$.pipe(map(({ models }) => models)),
    // In viewpoint mode the loaded components and the virtual (collapsed-path)
    // model live on the scope data, not the backbone collections. Threading
    // loadedGraph$ in also makes scope-data changes a CSS trigger.
    loadedGraph: loadedGraph$,
    formattingFilters: conditionalFormattingFilters$,
  }).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  const cssTriggers$ = merge(
    cssStateInputs$,
    action$.pipe(
      ofType(applicationStarted, diffModeUpdated),
      withLatestFrom(cssStateInputs$),
      map(([, state]) => state)
    )
  );

  cssTriggers$
    .pipe(
      debounceTime(50),
      map(toStylesForWorkspaces),
      distinctUntilChanged(isEqual),
      // No explicit clearStyles(FILTER_STYLES_KEY) needed before addStyles:
      // toStylesForWorkspaces always emits an entry for FILTER_STYLES_KEY and
      // handleAddToStyleSheet uses Map.set / Map.delete on each entry, so the
      // prior value is always overwritten.
      tap(styles => dispatchAction(addStylesToStyleSheet(styles)))
    )
    .subscribe();
};

const toStylesForWorkspaces = ({
  workspaceIds,
  linkedWorkspaces,
  workspaceAndModel,
  components,
  loadedGraph,
  formattingFilters,
}: CssStateInputs) => {
  const workspaceAndTypes = toResolvedWorkspacesWithTypes(
    workspaceIds,
    linkedWorkspaces,
    workspaceAndModel
  );

  // In viewpoint mode the components come from the scope data on the
  // loadedGraph; otherwise from the backbone collections. (Workspace/reference
  // *type* CSS keeps using workspaceAndModel$, which is populated in both
  // modes.) Virtual (collapsed-path) reference types only exist in viewpoint
  // mode and live on the scope data's synthetic virtual model.
  const { isViewpointMode, scopeData } = loadedGraph;
  const componentsToStyle = isViewpointMode ? scopeData.components : components;
  const virtualReferenceTypes = isViewpointMode
    ? Object.values(
        scopeData.modelsById[VIRTUAL_MODEL_ID]?.referenceTypes ?? {}
      )
    : [];

  // Connected workspaces are needed to add coloring for backlinked/linked
  // workspaces
  const workspaceStyleSheet = resolveWorkspaceStyleArgs(
    workspaceAndTypes,
    componentsToStyle
  ).flatMap(toWorkspaceStyleSheet);

  return [
    workspaceStyleSheet,
    [
      toModelStyleSheetEntry(
        virtualModelAsModelStyleArgs(virtualReferenceTypes)
      ),
    ],
    [formattingFilters]
      .map(toFormattingStyleSheetEntryArgs)
      .map(toFormattingStyleSheetEntry),
  ];
};

const toWorkspaceStyleSheet = ({
  componentStyleArgs,
  modelStyleArgs,
}: WorkspaceStyleArgs): Array<StyledEntityCss | ResetEntityCss> => {
  return [
    ...[modelStyleArgs].map(toModelStyleSheetEntry),
    ...componentStyleArgs.map(toComponentStyleSheetEntry),
  ];
};

const isColor = (color: unknown): color is Color => {
  return isString(color) && color.length > 0;
};

/**
 * Communicates that a component doesn't have directly associated styles.
 * If a component goes from having styles to not having styles, this has the
 * effect of removing the styles from the stylesheet.
 */
type UnstyledComponentStyleSheetArgs = {
  key: string;
};
type StyledComponentStyleSheetArgs = {
  key: string;
  color: string;
};
type ComponentStyleSheetArgs =
  | StyledComponentStyleSheetArgs
  | UnstyledComponentStyleSheetArgs;

const isStyledComponentStyleSheetArgs = (
  args: StyledComponentStyleSheetArgs | UnstyledComponentStyleSheetArgs
): args is StyledComponentStyleSheetArgs => {
  return isColor((args as StyledComponentStyleSheetArgs).color);
};

export const resolveComponentStyleSheetEntryArgs = (
  component: APIComponentAttributes
): ComponentStyleSheetArgs => {
  const key = entityCssOperations.getEntityCSSKey(component);
  const color = component.color;

  // Clear styles, if set
  if (!color) return { key };

  return { key, color };
};

export const toComponentStyleSheetEntry = (
  args: ComponentStyleSheetArgs
): StyledEntityCss | ResetEntityCss => {
  if (isStyledComponentStyleSheetArgs(args)) {
    const { key, color } = args;
    const cssEntries = entityCssOperations.getCSSForComp(key, color);
    return [key, cssEntries];
  }

  return [args.key];
};

type FormattingStyleSheetArgs = {
  key: string;
  colors: FormattingColors;
};

const toFormattingStyleSheetEntryArgs = (
  formattingFilters: ConditionalFormattingAttributes[]
): FormattingStyleSheetArgs => {
  // Make sure to include tags for both component and reference filters, as they can be used in both contexts
  const componentFilters = formattingFilters.filter(
    filterOperations.isComponentFilter
  );
  const referenceFilters = formattingFilters.filter(
    filterOperations.isReferenceFilter
  );

  return {
    key: FILTER_STYLES_KEY,
    colors: {
      componentColors: componentFilters.flatMap(filterOperations.getColors),
      referenceColors: referenceFilters.flatMap(filterOperations.getColors),
    },
  };
};

const toFormattingStyleSheetEntry = ({
  key,
  colors,
}: FormattingStyleSheetArgs): StyledEntityCss => {
  const cssEntries = entityCssOperations.toFormattingStyles(colors);
  return [key, cssEntries];
};

type ModelStyleArgs = {
  modelId: ArdoqId;
  componentTypes: APIComponentType[];
  referenceTypes: APIReferenceType[];
};

export const toModelStyleSheetEntryArgs = (
  model: APIModelAttributes
): ModelStyleArgs => {
  const { componentTypesById, referenceTypesById } =
    modelOperations.getModelTypes(model);

  const componentTypes = Object.values(componentTypesById);
  const referenceTypes = Object.values(referenceTypesById);

  return {
    modelId: model._id,
    componentTypes,
    referenceTypes,
  };
};

export const toModelStyleSheetEntry = ({
  modelId,
  componentTypes,
  referenceTypes,
}: ModelStyleArgs): StyledEntityCss => {
  const cssEntries = [
    ...componentTypes.flatMap(type =>
      entityCssOperations.toComponentTypeStyles(
        type.id,
        type.color || getDefaultCSSColor(type.level)
      )
    ),
    ...referenceTypes.flatMap(modelReferenceType =>
      entityCssOperations.referenceTypeStyles(modelId, modelReferenceType)
    ),
  ];
  return [modelId, cssEntries];
};
